import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface F104Report {
  header: { ruc: string; razon_social: string; anio: string; mes: string; period_label: string };
  ventas: {
    casillero_401: string;
    casillero_403: string;
    casillero_411: string;
    casillero_413: string;
    casillero_421: string;
    casillero_423: string;
    casillero_459: string;
  };
  compras: {
    casillero_501: string;
    casillero_503: string;
    casillero_511: string;
    casillero_513: string;
    casillero_559: string;
  };
  retenciones_recibidas: { casillero_619: string };
}

const fmt = (cents: string): string => {
  const n = BigInt(cents);
  const sign = n < 0n ? '-' : '';
  const abs = n < 0n ? -n : n;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
};

export function F104Page() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [creditoAnterior, setCreditoAnterior] = useState('0');
  const [establishmentCode, setEstablishmentCode] = useState<string>('');

  const { data: epList } = useQuery({
    queryKey: ['emission-points', activeOrgId],
    queryFn: () => api<Array<{ establishment_code: string }>>('/bookkeeping/emission-points'),
    enabled: !!activeOrgId,
  });
  const uniqueEstabs = Array.from(
    new Set((epList ?? []).map((ep) => ep.establishment_code)),
  ).sort();

  // Load credito_anterior from localStorage on mount/period change
  useEffect(() => {
    if (!activeOrgId) return;
    const key = `f104:${activeOrgId}:${year}:${month}:credito_anterior`;
    const v = localStorage.getItem(key);
    setCreditoAnterior(v ?? '0');
  }, [activeOrgId, year, month]);

  const handleCreditoChange = (v: string) => {
    setCreditoAnterior(v);
    if (activeOrgId) {
      const key = `f104:${activeOrgId}:${year}:${month}:credito_anterior`;
      localStorage.setItem(key, v);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['f104', activeOrgId, year, month, establishmentCode],
    queryFn: () =>
      api<F104Report>(
        `/bookkeeping/reports/f104?year=${year}&month=${month}${establishmentCode ? `&establishment_code=${establishmentCode}` : ''}`,
      ),
    enabled: !!activeOrgId,
  });

  // Liquidación derived client-side
  const casillero601 = data
    ? BigInt(data.ventas.casillero_411) + BigInt(data.ventas.casillero_413)
    : 0n;
  const credito605 = (() => {
    try {
      return BigInt(creditoAnterior || '0');
    } catch {
      return 0n;
    }
  })();
  const casillero619 = data ? BigInt(data.retenciones_recibidas.casillero_619) : 0n;
  // 609 = 601 - IVA pagado (513) - retenciones recibidas (619) - crédito 605
  const casillero609 = data
    ? casillero601 - BigInt(data.compras.casillero_513) - casillero619 - credito605
    : 0n;
  const casillero621 = casillero609; // identical for MVP — no manual ajustes

  return (
    <div className="space-y-4 print:space-y-2">
      <div className="flex items-center justify-between print:block">
        <h1 className="text-xl font-semibold">
          {t('reports.f104.title')} — {data?.header.period_label ?? '...'}
        </h1>
        <div className="flex items-center gap-2 print:hidden">
          <input
            type="number"
            value={year}
            min={2020}
            max={2100}
            onChange={(e) => setYear(Number.parseInt(e.target.value, 10))}
            className="w-24 rounded border px-2 py-1 text-sm"
            aria-label={t('reports.f104.year')}
          />
          <select
            value={month}
            onChange={(e) => setMonth(Number.parseInt(e.target.value, 10))}
            className="rounded border px-2 py-1 text-sm"
            aria-label={t('reports.f104.month')}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
          {uniqueEstabs.length >= 2 && (
            <select
              value={establishmentCode}
              onChange={(e) => setEstablishmentCode(e.target.value)}
              className="rounded border px-2 py-1 text-sm"
              aria-label={t('reports.filter_establishment')}
            >
              <option value="">{t('reports.filter_establishment_all')}</option>
              {uniqueEstabs.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            {t('reports.f104.print')}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(error as { message?: string }).message ?? 'Error'}
        </p>
      )}

      {data && (
        <>
          <div className="rounded border bg-white p-3 text-sm">
            <span className="text-slate-500">{t('reports.f104.ruc')}: </span>
            <span className="font-mono font-semibold">{data.header.ruc}</span>
            <span className="ml-3 text-slate-500">{t('reports.f104.razon')}: </span>
            <span className="font-semibold">{data.header.razon_social}</span>
          </div>

          <Section title={t('reports.f104.sections.ventas')}>
            <Row
              code="401"
              label={t('reports.f104.casilleros.c401')}
              value={fmt(data.ventas.casillero_401)}
            />
            <Row
              code="403"
              label={t('reports.f104.casilleros.c403')}
              value={fmt(data.ventas.casillero_403)}
            />
            <Row
              code="411"
              label={t('reports.f104.casilleros.c411')}
              value={fmt(data.ventas.casillero_411)}
            />
            <Row
              code="413"
              label={t('reports.f104.casilleros.c413')}
              value={fmt(data.ventas.casillero_413)}
            />
            <Row
              code="421"
              label={t('reports.f104.casilleros.c421')}
              value={fmt(data.ventas.casillero_421)}
            />
            <Row
              code="423"
              label={t('reports.f104.casilleros.c423')}
              value={fmt(data.ventas.casillero_423)}
            />
            <Row
              code="459"
              label={t('reports.f104.casilleros.c459')}
              value={fmt(data.ventas.casillero_459)}
              bold
            />
          </Section>

          <Section title={t('reports.f104.sections.compras')}>
            <Row
              code="501"
              label={t('reports.f104.casilleros.c501')}
              value={fmt(data.compras.casillero_501)}
            />
            <Row
              code="503"
              label={t('reports.f104.casilleros.c503')}
              value={fmt(data.compras.casillero_503)}
            />
            <Row
              code="511"
              label={t('reports.f104.casilleros.c511')}
              value={fmt(data.compras.casillero_511)}
            />
            <Row
              code="513"
              label={t('reports.f104.casilleros.c513')}
              value={fmt(data.compras.casillero_513)}
            />
            <Row
              code="559"
              label={t('reports.f104.casilleros.c559')}
              value={fmt(data.compras.casillero_559)}
              bold
            />
          </Section>

          <Section title={t('reports.f104.sections.liquidacion')}>
            <Row
              code="601"
              label={t('reports.f104.casilleros.c601')}
              value={fmt(casillero601.toString())}
            />
            <div className="flex items-center justify-between border-b py-2 text-sm">
              <span>
                <span className="font-mono text-xs text-slate-400">605</span>{' '}
                {t('reports.f104.casilleros.c605')}
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={creditoAnterior}
                onChange={(e) => handleCreditoChange(e.target.value)}
                className="w-32 rounded border px-2 py-1 text-right font-mono text-sm print:border-0"
                aria-label={t('reports.f104.casilleros.c605')}
              />
            </div>
            <p className="px-2 text-xs text-slate-400 print:hidden">
              {t('reports.f104.casillero_605_hint')}
            </p>
            <Row
              code="619"
              label={t('reports.f104.casilleros.c619')}
              value={fmt(data.retenciones_recibidas.casillero_619)}
            />
            <Row
              code="609"
              label={t('reports.f104.casilleros.c609')}
              value={fmt(casillero609.toString())}
              bold
            />
            <Row
              code="621"
              label={t('reports.f104.casilleros.c621')}
              value={fmt(casillero621.toString())}
              bold
            />
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded border bg-white">
      <h2 className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">{title}</h2>
      <div className="px-3 py-2">{children}</div>
    </div>
  );
}

function Row({
  code,
  label,
  value,
  bold,
}: {
  code: string;
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between border-b py-1 text-sm last:border-0 ${bold ? 'font-semibold' : ''}`}
    >
      <span>
        <span className="font-mono text-xs text-slate-400">{code}</span> {label}
      </span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
